import { AlertType } from '../../types/alerts';
import { Brain, BookOpen, GitBranch, Wand2 } from 'lucide-react';

interface AlertTypeSelectorProps {
  selectedType: AlertType | null | 'automatic';
  onTypeChange: (type: AlertType | null | 'automatic') => void;
  showAutomatic?: boolean;
}

const alertTypes: { type: AlertType; icon: typeof Brain; label: string; color: string }[] = [
  { type: 'behaviour', icon: Brain, label: 'Behaviour', color: 'text-purple-500' },
  { type: 'rule', icon: BookOpen, label: 'Rule', color: 'text-blue-500' },
  { type: 'sequential', icon: GitBranch, label: 'Sequential', color: 'text-primary' },
];

const createAlertTypes: { type: AlertType | 'automatic'; icon: typeof Brain; label: string; color: string }[] = [
  { type: 'automatic', icon: Wand2, label: 'Automatic', color: 'text-amber-500' },
  { type: 'behaviour', icon: Brain, label: 'Behaviour', color: 'text-purple-500' },
  { type: 'rule', icon: BookOpen, label: 'Rule', color: 'text-blue-500' },
  { type: 'sequential', icon: GitBranch, label: 'Sequential', color: 'text-primary' },
];

export function AlertTypeSelector({ selectedType, onTypeChange, showAutomatic = false }: AlertTypeSelectorProps) {
  const types = showAutomatic ? createAlertTypes : alertTypes;
  
  const handleClick = (type: AlertType | 'automatic') => {
    // For automatic mode, don't allow toggle off
    if (showAutomatic) {
      onTypeChange(type);
      return;
    }
    // Toggle: if already selected, unselect (null means show all)
    if (selectedType === type) {
      onTypeChange(null);
    } else {
      onTypeChange(type);
    }
  };

  return (
    <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-xl">
      {types.map((alertType) => {
        const Icon = alertType.icon;
        const isSelected = selectedType === alertType.type;
        return (
          <button
            key={alertType.type}
            onClick={() => handleClick(alertType.type)}
            className={`
              relative flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-300
              ${isSelected 
                ? 'bg-gradient-to-br from-card to-card/80 text-foreground shadow-sm ring-1 ring-border/50 scale-105' 
                : 'text-muted-foreground hover:text-foreground hover:bg-card/50 hover:scale-105'
              }
            `}
          >
            <Icon 
              className={`h-4 w-4 transition-all duration-300 ${alertType.color} ${
                isSelected ? '' : 'opacity-40'
              }`}
            />
            <span>{alertType.label}</span>
          </button>
        );
      })}
    </div>
  );
}
